import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, FolderOpen, ClipboardList, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearch, type SearchResult, type SearchResults } from '../../hooks/useSearch.js';
import { useRecentStore } from '../../stores/recent.js';
import { useWorkspaceStore } from '../../stores/workspace.js';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PaletteItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  action: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function flattenResults(r: SearchResults): SearchResult[] {
  return [...r.companies, ...r.contacts, ...r.buckets, ...r.orders];
}

// ── Row components ─────────────────────────────────────────────────────────────

function PaletteRow({
  label,
  subtitle,
  icon,
  isSelected,
  onSelect,
}: {
  label: string;
  subtitle?: string | undefined;
  icon?: React.ReactNode;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onMouseDown={(e) => { e.preventDefault(); onSelect(); }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        padding: 'var(--space-2) var(--space-4)',
        background: isSelected ? 'var(--color-bg-subtle)' : 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {icon && (
        <span style={{ color: 'var(--color-text-muted)', display: 'flex', flexShrink: 0 }}>{icon}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </p>
        {subtitle && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </p>
        )}
      </div>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 'var(--space-2) var(--space-4) var(--space-1)', borderTop: '1px solid var(--color-border)' }}>
      <p style={{ fontSize: '11px', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {children}
      </p>
    </div>
  );
}

// ── CommandPalette ─────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { t } = useTranslation('search');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const { items: recentItems, addItem: addRecent } = useRecentStore();

  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { results, isLoading } = useSearch(query);
  const searchFlat = flattenResults(results);
  const hasQuery = query.trim().length > 0;
  const hasResults = searchFlat.length > 0;

  const bucketLabel = workspace?.bucketLabel ?? 'Project';
  const orderLabel = workspace?.orderLabel ?? 'Order';

  const quickActions: PaletteItem[] = [
    {
      id: 'new-company',
      label: t('quick_actions.new_company'),
      icon: <Building2 size={15} />,
      action: () => { navigate('/companies'); onClose(); },
    },
    {
      id: 'new-bucket',
      label: t('quick_actions.new_bucket', { label: bucketLabel }),
      icon: <FolderOpen size={15} />,
      action: () => { navigate('/projects'); onClose(); },
    },
    {
      id: 'new-order',
      label: t('quick_actions.new_order', { label: orderLabel }),
      icon: <ClipboardList size={15} />,
      action: () => { navigate('/orders'); onClose(); },
    },
    {
      id: 'settings',
      label: t('quick_actions.go_settings'),
      icon: <Settings2 size={15} />,
      action: () => { navigate('/settings'); onClose(); },
    },
  ];

  // All navigable items for keyboard nav
  const allItems: { action: () => void }[] = hasQuery
    ? searchFlat.map((r) => ({
        action: () => {
          addRecent({ id: r.id, type: r.type, title: r.title, url: r.url });
          navigate(r.url);
          onClose();
        },
      }))
    : [
        ...quickActions.map((a) => ({ action: a.action })),
        ...recentItems.map((r) => ({
          action: () => {
            addRecent({ id: r.id, type: r.type, title: r.title, url: r.url });
            navigate(r.url);
            onClose();
          },
        })),
      ];

  const totalItems = allItems.length;

  const handleSelect = useCallback((idx: number) => {
    allItems[idx]?.action();
    setQuery('');
    setSelectedIdx(0);
  }, [allItems]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Reset selection when results change
  useEffect(() => { setSelectedIdx(0); }, [results]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(selectedIdx);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  // ── Render ──

  let itemOffset = 0;

  const renderSearchResults = () => {
    const sections: React.ReactNode[] = [];
    const sectionDefs: { key: keyof SearchResults; label: string }[] = [
      { key: 'companies', label: t('sections.companies') },
      { key: 'contacts', label: t('sections.contacts') },
      { key: 'buckets', label: t('sections.buckets') },
      { key: 'orders', label: t('sections.orders') },
    ];

    for (const { key, label } of sectionDefs) {
      const rows = results[key] as SearchResult[];
      if (rows.length === 0) continue;

      sections.push(
        <div key={key}>
          <SectionLabel>{label}</SectionLabel>
          {rows.map((r) => {
            const idx = itemOffset++;
            return (
              <PaletteRow
                key={r.id}
                label={r.title}
                subtitle={[r.subtitle, r.meta].filter(Boolean).join(' · ') || undefined}
                isSelected={idx === selectedIdx}
                onSelect={() => handleSelect(idx)}
              />
            );
          })}
        </div>,
      );
    }
    return sections;
  };

  const renderDefault = () => {
    const sections: React.ReactNode[] = [];

    sections.push(
      <div key="quick-actions">
        <SectionLabel>{t('sections.quick_actions')}</SectionLabel>
        {quickActions.map((action) => {
          const idx = itemOffset++;
          return (
            <PaletteRow
              key={action.id}
              label={action.label}
              icon={action.icon}
              isSelected={idx === selectedIdx}
              onSelect={() => handleSelect(idx)}
            />
          );
        })}
      </div>,
    );

    if (recentItems.length > 0) {
      sections.push(
        <div key="recent">
          <SectionLabel>{t('sections.recent')}</SectionLabel>
          {recentItems.map((item) => {
            const idx = itemOffset++;
            return (
              <PaletteRow
                key={`${item.type}-${item.id}`}
                label={item.title}
                subtitle={item.type}
                isSelected={idx === selectedIdx}
                onSelect={() => handleSelect(idx)}
              />
            );
          })}
        </div>,
      );
    }

    return sections;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 300,
        }}
      />

      {/* Palette */}
      <div
        style={{
          position: 'fixed',
          top: '20vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 560,
          maxWidth: 'calc(100vw - 32px)',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          zIndex: 301,
          overflow: 'hidden',
          maxHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border)' }}>
          <Search size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('placeholder_palette')}
            style={{
              flex: 1, border: 'none', background: 'none', outline: 'none',
              fontSize: 'var(--text-base)', color: 'var(--color-text)',
            }}
          />
          <kbd style={{ fontSize: 11, color: 'var(--color-text-muted)', background: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '2px 6px' }}>
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {hasQuery && isLoading && (
            <p style={{ padding: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>…</p>
          )}

          {hasQuery && !isLoading && !hasResults && (
            <div style={{ padding: 'var(--space-4)' }}>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                {t('no_results.title', { query })}
              </p>
              <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 1.8 }}>
                <li>{t('no_results.tip_typos')}</li>
                <li>{t('no_results.tip_order')}</li>
                <li>{t('no_results.tip_email')}</li>
              </ul>
            </div>
          )}

          {hasQuery && !isLoading && hasResults && renderSearchResults()}

          {!hasQuery && renderDefault()}
        </div>
      </div>
    </>
  );
}
